// Catalog.ID Client — `chat_address` claim create + share
// ============================================================================
// The side-effecting half of the `create-chat-address` key-grant mode, kept out
// of `chat-address-handlers.ts` so the gate logic there stays unit-testable with
// no network and no modals — the `ceremony-keystore.ts` split, for the same
// reason.
//
// `chat_address` is created PROGRAMMATICALLY, never by the generic claim form:
// it is deliberately absent from the creatable-type list in
// `pane-types/claims-list-pane.ts` (a display label only), exactly like
// `registry_account_stream`. contact-flow.md §8 states the rule; this module is
// the only writer.
//
// IDEMPOTENCY IS THE POINT. The contact flow retries the share on every
// connected transition (pane-protocol.md §4.5), crossed accepts run the ceremony
// on both sides, and the same peer may be re-accepted after a decline. So:
//
//   * an existing `chat_address` claim holding EXACTLY this value is reused —
//     never duplicated. One reachability record per account is what a reader
//     expects; two would leave "which one is current" undecidable, the same
//     failure `saveEnrollmentRecord` supersedes records to avoid.
//   * an existing share to the peer is success, not a duplicate write.
//
// A value MISMATCH (the operator changed, the chat_encrypt key rotated) is a
// NEW claim, not an update: `PUT /api/claims/{uuid}` rewrites only
// `{encryptedValue, nonce}` and keeps the original wrapped symmetric key, so
// every existing recipient of the old address would silently start reading the
// new one. Reachability changes must be a fresh consent decision per peer.
// Stale claims are left in place — revoking them is the user's call (§8: revoke
// + unshare-all), never a side effect of a handshake.

import * as accountApi from '../api/account';
import * as claimsApi from '../api/claims';
import * as aes from '../crypto/aes';
import { encryptClaim, unwrapSymmetricKeyChain } from '../crypto/claims';
import { keys } from '../state/keys';
import {
    ATTR_ENCRYPT_KEY_ID, ClaimShareError, shareClaimWithRecipient,
} from '../state/claim-sharing';

/** The CID claim type carrying a chat reachability record (contact-flow.md §8). */
export const CHAT_ADDRESS_CLAIM_TYPE = 'chat_address';

export interface CreateAndShareChatAddressInput {
    /** The account to share with. */
    peerCid: string;
    /** JCS canonical JSON of the `chat_address.v1` record — opaque here. */
    chatAddressValue: string;
    /** Human label for the claims list. Encrypted under the claim's own key. */
    label?: string;
}

export interface CreateAndShareChatAddressResult {
    /** Server-assigned. NEVER predicted (migration §17, ownership D1). */
    claimUuid: string;
    /** True once the peer holds a key row — whether written now or already there. */
    shared: boolean;
    /** False when an existing claim with this exact value was reused. */
    created: boolean;
    /** True when the peer already had a share before this call. */
    alreadyShared: boolean;
}

/**
 * Find-or-create this account's `chat_address` claim for `chatAddressValue`,
 * then share it with `peerCid`. Idempotent on both halves.
 */
export async function createAndShareChatAddress(
    input: CreateAndShareChatAddressInput,
): Promise<CreateAndShareChatAddressResult> {
    const existing = await findChatAddressClaim(input.chatAddressValue);

    let claimUuid: string;
    let created: boolean;
    if (existing) {
        claimUuid = existing;
        created = false;
    } else {
        claimUuid = await createChatAddressClaim(input.chatAddressValue, input.label);
        created = true;
    }

    // Re-read rather than trusting the create response: the share step needs
    // the `self` key row, and on the reuse path it needs the existing share
    // rows too.
    const detail = await claimsApi.getClaim(claimUuid);
    const outcome = await shareClaimWithRecipient(claimUuid, input.peerCid, detail);

    return {
        claimUuid,
        shared: true,
        created,
        alreadyShared: outcome === 'already-shared',
    };
}

/**
 * The uuid of an existing `chat_address` claim whose decrypted value is byte-
 * identical to `chatAddressValue`, or null.
 *
 * Values are compared after decryption, not by ciphertext: every claim gets a
 * fresh symmetric key and nonce, so two claims holding the same address have
 * completely different ciphertext.
 */
export async function findChatAddressClaim(chatAddressValue: string): Promise<string | null> {
    const summaries = await claimsApi.listClaims();
    const candidates = summaries.filter(
        c => c.claimType === CHAT_ADDRESS_CLAIM_TYPE && !c.revokedAt);

    for (const summary of candidates) {
        try {
            const detail = await claimsApi.getClaim(summary.claimUuid);
            const value = await decryptOwnClaimValue(detail);
            if (value === chatAddressValue) return summary.claimUuid;
        } catch {
            // One unreadable claim (a wrap this device cannot peel) must not
            // hide the others, and must not turn a reuse into a duplicate-free
            // failure — fall through and let the caller create a fresh one.
            continue;
        }
    }
    return null;
}

/** Decrypt a claim this account owns, walking the attr_encrypt rotation chain. */
async function decryptOwnClaimValue(
    detail: { encryptedValue: string; nonce: string; keys: { recipientType: string; encryptedKey: string; keyIndex: number }[] },
): Promise<string> {
    const selfKey = detail.keys.find(k => k.recipientType === 'self');
    if (!selfKey) throw new ClaimShareError('claim-has-no-self-key', 'No self key found');

    const symKey = await unwrapSymmetricKeyChain(
        selfKey.encryptedKey, keys.getKeyChain(ATTR_ENCRYPT_KEY_ID), selfKey.keyIndex);
    const plaintext = await aes.decrypt(
        symKey, aes.fromBase64(detail.nonce), aes.fromBase64(detail.encryptedValue));
    return aes.decodeUtf8(plaintext);
}

/** Encrypt and POST a fresh `chat_address` claim. Returns the server's uuid. */
async function createChatAddressClaim(
    chatAddressValue: string, label?: string,
): Promise<string> {
    const pubKeys = await accountApi.getOwnPublicKeys();
    const attrPub = pubKeys.find(k => k.keyId === ATTR_ENCRYPT_KEY_ID);
    if (!attrPub) {
        throw new ClaimShareError('no-attr-encrypt-key', 'This account has no attr_encrypt key');
    }

    const encrypted = await encryptClaim(
        chatAddressValue, aes.fromBase64(attrPub.publicKey), label);

    const created = await claimsApi.createClaim({
        claimType: CHAT_ADDRESS_CLAIM_TYPE,
        encryptedValue: encrypted.encryptedValue,
        nonce: encrypted.nonce,
        encryptedKey: encrypted.encryptedKey,
        ...(encrypted.encryptedLabel ? { encryptedLabel: encrypted.encryptedLabel } : {}),
        ...(encrypted.labelNonce ? { labelNonce: encrypted.labelNonce } : {}),
    });
    return created.claimUuid;
}
